import { getChallengeStage } from './challengeConfig';
import { getMonsterById, getMonsterDetailData } from './monsters';
import { EREBOS_MONSTER, EREBOS_FORM2_MONSTER } from './bossConfig';

const BOSS_MONSTERS = [EREBOS_MONSTER, EREBOS_FORM2_MONSTER];

function getBossById(id: string) {
  return BOSS_MONSTERS.find((m) => m.id === id);
}

/** 敵キャラ紹介データ */
export interface EnemyIntroduction {
  /** ステージ番号 */
  stageNumber: number;
  /** 敵の個体名 */
  enemyName: string;
  /** 敵の魂格名 */
  enemySpecies: string;
  /** 敵モンスターID */
  enemyMonsterId: string;
  /** 魂格の説明文 */
  description: string;
  /** 戦法ヒント */
  battleHint: string;
  /** 得意距離 */
  preferredRange: string;
  /** 長所 */
  strengths: string;
  /** 弱点 */
  weaknesses: string;
}

/** ステージ別の説明文オーバーライド（speciesDescriptionを上書きしたいステージ） */
const STAGE_DESCRIPTION_OVERRIDE: Record<number, string> = {
  9: '封印が解かれた禁忌の存在が、導き手の前に立ちはだかる。\nここまで積み上げてきたすべてを出し尽くせ。\n3体のパーティを編成して挑め。',
};

/** ステージ別の長所・弱点定義 */
const STAGE_HINTS: Record<number, { strengths: string; weaknesses: string }> = {
  1: {
    strengths: '腕力が高い・打たれ強い',
    weaknesses: '素早さが低い・動きが単調',
  },
  2: {
    strengths: 'HP最大級・長期戦向き',
    weaknesses: '特殊攻撃・素早さが低い',
  },
  3: {
    strengths: '特殊攻撃連打が強力',
    weaknesses: 'リフレクターで反射される',
  },
  4: {
    strengths: '防御が非常に高い',
    weaknesses: '特殊攻撃が有効・素早さ低い',
  },
  5: {
    strengths: '回避が高い・素早い',
    weaknesses: 'HPが低め・近距離は苦手',
  },
  6: {
    strengths: '物理攻撃が超強力',
    weaknesses: '遠距離・特殊攻撃には弱い',
  },
  7: {
    strengths: '遠距離の特殊攻撃が猛威',
    weaknesses: '近距離に持ち込めば有利',
  },
  8: {
    strengths: 'バランス型・総合力高い',
    weaknesses: '弱点がない。読み合いで勝負',
  },
  9: {
    strengths: '全能力が最高水準・特殊耐性あり',
    weaknesses: '3体の力を合わせ、読み合いで活路を開け',
  },
};

/**
 * ステージ番号から敵キャラ紹介データを取得する
 */
export function getEnemyIntroduction(stageNumber: number): EnemyIntroduction | undefined {
  const stage = getChallengeStage(stageNumber);
  if (!stage) return undefined;

  const monster = getMonsterById(stage.enemyMonsterId) ?? getBossById(stage.enemyMonsterId);
  const detail = getMonsterDetailData(stage.enemyMonsterId);
  const hints = STAGE_HINTS[stageNumber];
  if (!monster || !detail || !hints) return undefined;

  return {
    stageNumber,
    enemyName: monster.name,
    enemySpecies: monster.species,
    enemyMonsterId: stage.enemyMonsterId,
    description: STAGE_DESCRIPTION_OVERRIDE[stageNumber] ?? detail.speciesDescription,
    battleHint: detail.battleHint,
    preferredRange: detail.preferredRange,
    strengths: hints.strengths,
    weaknesses: hints.weaknesses,
  };
}
